import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errMsg, GRADE_BANDS } from "@/lib/api";
import { PageHeader, EmptyState, Loading, GradeBadge, FrameworkBadge } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Library, Sparkles, BookOpen } from "lucide-react";
import { toast } from "sonner";

const COLLECTION_LABELS = {
  all: "All programs",
  Core: "Core courses",
  "Texas TEKS": "Texas TEKS & STAAR",
  AP: "AP exam prep",
  "SAT & ACT": "SAT & ACT prep",
  GED: "GED prep",
  Professional: "Professional certification",
};

export default function Catalog() {
  const { user } = useAuth();
  const [grade, setGrade] = useState(user.role === "student" ? user.grade_band : "all");
  const [subject, setSubject] = useState("all");
  const [q, setQ] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [collections, setCollections] = useState([]);
  const [collection, setCollection] = useState("all");
  const [courses, setCourses] = useState(null);

  useEffect(() => {
    api.get("/catalog/meta").then(({ data }) => {
      setSubjects(data.subjects);
      setCollections(data.collections || []);
    });
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      const params = {};
      if (grade !== "all") params.grade_band = grade;
      if (subject !== "all") params.subject = subject;
      if (q.trim()) params.q = q.trim();
      if (collection !== "all") params.collection = collection;
      api.get("/courses", { params }).then(({ data }) => setCourses(data)).catch((e) => toast.error(errMsg(e)));
    }, 250);
    return () => clearTimeout(t);
  }, [grade, subject, q, collection]);

  return (
    <div>
      <PageHeader
        eyebrow="Course library"
        title="Explore courses"
        subtitle="Starter courses for every grade, plus courses written by teachers. Can't find a topic? Create one with AI."
        actions={<Button asChild data-testid="catalog-create-button"><Link to="/create"><Sparkles className="h-4 w-4 mr-2" />Create with AI</Link></Button>}
      />

      <div className="bg-white border border-border rounded-[14px] p-4 mb-6 space-y-4">
        {collections.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="catalog-collection-tabs">
            {["all", ...collections].map((c) => (
              <button
                key={c}
                onClick={() => setCollection(c)}
                data-testid={`catalog-collection-${c.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                className={`h-9 px-4 rounded-full text-sm font-medium border transition-colors duration-150 ${
                  collection === c ? "bg-[#123645] text-white border-[#123645]" : "bg-white border-border hover:bg-[#EEF2F4]"
                }`}
              >
                {COLLECTION_LABELS[c] || c}
              </button>
            ))}
          </div>
        )}
        <div className="flex flex-wrap gap-2" data-testid="catalog-grade-band-tabs">
          {["all", ...GRADE_BANDS].map((g) => (
            <button
              key={g}
              onClick={() => setGrade(g)}
              data-testid={`catalog-grade-${g}`}
              className={`h-9 px-4 rounded-full text-sm font-medium border transition-colors duration-150 ${
                grade === g ? "bg-[#0F7B7D] text-white border-[#0F7B7D]" : "bg-[#FBFAF4] border-border hover:bg-[#D7F3F1]"
              }`}
            >
              {g === "all" ? "All grades" : g}
            </button>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search courses" className="pl-9 h-11 bg-[#FBFAF4]" data-testid="catalog-search-input" />
          </div>
          <Select value={subject} onValueChange={setSubject}>
            <SelectTrigger className="sm:w-56 h-11 bg-[#FBFAF4]" data-testid="catalog-subject-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All subjects</SelectItem>
              {subjects.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!courses ? (
        <Loading />
      ) : courses.length === 0 ? (
        <EmptyState icon={Library} title="No courses match" text="Try a different grade or subject, or create a lesson on this topic with AI." action={<Button asChild><Link to="/create">Create with AI</Link></Button>} testid="catalog-empty" />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6" data-testid="catalog-course-grid">
          {courses.map((c) => {
            const pct = c.lesson_count ? Math.round((c.completed_count * 100) / c.lesson_count) : 0;
            return (
              <Link key={c.course_id} to={`/courses/${c.course_id}`} className="bg-white border border-border rounded-[14px] p-5 lift flex flex-col" data-testid="catalog-course-card">
                <div className="flex items-center gap-2 flex-wrap">
                  <GradeBadge grade={c.grade_band} />
                  <span className="text-xs text-muted-foreground">{c.subject}</span>
                  {c.source === "teacher" && <span className="text-xs text-[#1D4F91] bg-[#DCEBFF] rounded-full px-2 py-0.5">by {c.author_name}</span>}
                </div>
                <FrameworkBadge item={c} className="mt-2 self-start" />
                <div className="mt-3 font-display font-semibold text-lg text-[#071A22]">{c.title}</div>
                <p className="text-sm text-muted-foreground mt-1 flex-1">{c.description}</p>
                <div className="mt-4 flex items-center gap-3 text-xs text-muted-foreground">
                  <BookOpen className="h-3.5 w-3.5" /> {c.lesson_count} lessons
                  {c.completed_count > 0 && <span className="ml-auto text-[#14663A] font-medium">{pct}% done</span>}
                </div>
                {c.completed_count > 0 && <Progress value={pct} className="h-1.5 mt-2" />}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
